import { useState } from 'react';
import { api } from '../lib/api';
import { useAsync } from '../lib/useAsync';
import { USD, fullDate, magnitude } from '../lib/format';
import { C, ink, text } from '../lib/theme';
import type { EventType, Filters, LostEvent, Reason } from '../lib/types';
import { ScaleModal, type Origin } from './ScaleModal';
import { ErrorBanner, Skeleton } from './primitives';
import { IconCheck, IconLink, IconUndo } from './icons';
import { ReasonTagPicker } from './ReasonTagPicker';

/**
 * Painel de auditoria expandido — nasce do botão "Auditar" quando o evento
 * tem sinal (Churn Revive detectado, ou cliente com histórico correlacionado
 * no período), em vez de um botão novo solto na fila (pedido explícito).
 * Reúne: contexto do cliente, motivo (filtrado por tipo), e a conciliação
 * manual churn ⇄ contraction — sem nenhuma consulta automática à Nekt/Stripe,
 * a checagem é sempre do humano.
 */
export function EventAuditModal({ ev, reasons, filters, origin, confirmado, onClose, onAudit, onPickReason, onSetCorrection, onClearCorrection }: {
  ev: LostEvent;
  reasons: Reason[];
  filters: Filters;
  origin: Origin | null;
  confirmado: boolean;
  onClose: () => void;
  onAudit: (ev: LostEvent) => void;
  onPickReason: (ev: LostEvent, picked: Reason[]) => void;
  onSetCorrection: (ev: LostEvent, body: { corrected_event_type: 'churn' | 'contraction'; corrected_mrr_lost: number; note?: string | null }) => void;
  onClearCorrection: (ev: LostEvent) => void;
}) {
  const hasRelated = Number(ev.related_audited_count) > 0;
  const related = useAsync(
    () => (hasRelated ? api.relatedEvents(ev.id, filters.from, filters.to) : Promise.resolve([])),
    [ev.id, filters.from, filters.to, hasRelated],
  );

  const hasCorrection = ev.corrected_event_type !== null;
  const otherType: EventType = ev.event_type === 'churn' ? 'contraction' : 'churn';
  const [reclassifying, setReclassifying] = useState(false);
  const [correctedValue, setCorrectedValue] = useState(() => String(magnitude(ev.corrected_mrr_lost ?? ev.mrr_lost)));

  const effectiveType = ev.corrected_event_type ?? ev.event_type;
  const effectiveValue = ev.corrected_mrr_lost ?? ev.mrr_lost;

  return (
    <ScaleModal
      open={origin !== null} origin={origin} onClose={onClose}
      title={ev.master_id} subtitle={`${ev.product_category ?? '—'} · ${fullDate(ev.event_date)}`} width={560}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 12 }}>
          <span style={{ fontSize: 26, fontWeight: 800, color: C.churn, fontVariantNumeric: 'tabular-nums' }}>
            {USD(magnitude(effectiveValue))}
          </span>
          <span style={{ fontSize: 13, color: ink(0.5) }}>
            {effectiveType === 'churn' ? 'churn' : 'contraction'}
            {hasCorrection && <span style={{ color: C.accent, marginLeft: 6 }}>· reclassificado</span>}
          </span>
        </div>

        {hasRelated && (
          <div>
            <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: '0.12em', textTransform: 'uppercase', color: text.title, marginBottom: 8, display: 'flex', alignItems: 'center', gap: 6 }}>
              <IconLink size={11} />Eventos relacionados deste cliente no período
            </div>
            {related.loading && <Skeleton height={44} radius={10} />}
            {related.error && <ErrorBanner message={related.error} onRetry={related.reload} />}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
              {(related.data ?? []).map((r) => (
                <div key={r.id} style={{
                  display: 'flex', alignItems: 'center', gap: 10, padding: '9px 11px', borderRadius: 10,
                  background: ink(0.04), border: `1px solid ${ink(0.08)}`,
                }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 12, fontWeight: 650, color: ink(0.85) }}>
                      {r.event_type} · {r.product_category ?? '—'} · {fullDate(r.event_date)}
                    </div>
                    {(r.reason_label || r.audit_note) && (
                      <div style={{ fontSize: 11, color: ink(0.4), marginTop: 2 }}>
                        {r.reason_label ?? ''}{r.reason_label && r.audit_note ? ' · ' : ''}{r.audit_note ?? ''}
                      </div>
                    )}
                  </div>
                  <div style={{ fontSize: 12.5, fontWeight: 700, color: ink(0.75), fontVariantNumeric: 'tabular-nums', flex: 'none' }}>
                    {USD(magnitude(r.mrr_lost))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <div>
          <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: '0.12em', textTransform: 'uppercase', color: text.title, marginBottom: 8 }}>Motivos</div>
          <ReasonTagPicker
            inline reasons={reasons}
            value={ev.reason_ids?.length ? ev.reason_ids : (ev.reason_id != null ? [ev.reason_id] : [])}
            onChange={(picked) => onPickReason(ev, picked)}
          />
        </div>

        <div>
          <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: '0.12em', textTransform: 'uppercase', color: text.title, marginBottom: 8 }}>Conciliação</div>
          {!reclassifying && !hasCorrection && (
            <button
              type="button" onClick={() => setReclassifying(true)}
              style={{
                border: `1px solid ${ink(0.14)}`, background: ink(0.05), color: ink(0.72), cursor: 'pointer',
                font: 'inherit', fontSize: 12, fontWeight: 650, padding: '9px 13px', borderRadius: 10,
              }}
            >Reclassificar como {otherType === 'churn' ? 'Churn' : 'Contraction'}…</button>
          )}
          {!reclassifying && hasCorrection && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
              <span style={{ fontSize: 12.5, color: ink(0.75) }}>
                Reclassificado pra <strong style={{ color: C.accent }}>{ev.corrected_event_type}</strong>, valor {USD(magnitude(ev.corrected_mrr_lost))}
                {ev.correction_note ? ` — ${ev.correction_note}` : ''}
              </span>
              <button
                type="button" onClick={() => onClearCorrection(ev)}
                style={{
                  border: 'none', background: 'none', color: ink(0.45), cursor: 'pointer', font: 'inherit',
                  fontSize: 11.5, textDecoration: 'underline',
                }}
              >remover correção</button>
            </div>
          )}
          {reclassifying && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 9 }}>
              <div style={{ fontSize: 12, color: ink(0.55) }}>
                Virar <strong style={{ color: ink(0.85) }}>{otherType === 'churn' ? 'Churn' : 'Contraction'}</strong> — confira manualmente o valor da assinatura nova antes de salvar (sem checagem automática).
              </div>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <span style={{ fontSize: 13, color: ink(0.5) }}>US$</span>
                <input
                  type="number" step="0.01" value={correctedValue} onChange={(e) => setCorrectedValue(e.target.value)}
                  style={{
                    width: 110, boxSizing: 'border-box', font: 'inherit', fontSize: 13, padding: '8px 10px',
                    borderRadius: 9, border: `1px solid ${ink(0.16)}`, background: 'var(--input-bg)', color: C.fg,
                    outline: 'none',
                  }}
                />
                <button
                  type="button"
                  onClick={() => {
                    const value = Number(correctedValue);
                    if (Number.isFinite(value)) {
                      onSetCorrection(ev, { corrected_event_type: otherType, corrected_mrr_lost: -Math.abs(value) });
                      setReclassifying(false);
                    }
                  }}
                  style={{
                    border: 'none', background: `linear-gradient(150deg, ${C.accent}, ${C.accentDeep})`, color: '#fff',
                    cursor: 'pointer', font: 'inherit', fontSize: 12, fontWeight: 700, padding: '9px 14px', borderRadius: 9,
                  }}
                >Salvar</button>
                <button
                  type="button" onClick={() => setReclassifying(false)}
                  style={{ border: 'none', background: 'none', color: ink(0.45), cursor: 'pointer', font: 'inherit', fontSize: 12 }}
                >cancelar</button>
              </div>
            </div>
          )}
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end', paddingTop: 4, borderTop: `1px solid ${ink(0.07)}` }}>
          <button
            type="button" onClick={() => { onAudit(ev); onClose(); }}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 8, marginTop: 14,
              border: confirmado ? `1px solid ${ink(0.16)}` : 'none', cursor: 'pointer', font: 'inherit',
              fontSize: 13, fontWeight: 700, padding: '10px 18px', borderRadius: 11,
              background: confirmado ? ink(0.08) : `linear-gradient(150deg, ${C.accent}, ${C.accentDeep})`,
              color: confirmado ? ink(0.75) : '#fff',
            }}
          >{confirmado ? <><IconUndo size={14} />Reverter pra fila</> : <><IconCheck size={14} />Confirmar auditoria</>}</button>
        </div>
      </div>
    </ScaleModal>
  );
}
